import { CHAMBER_TYPE_ID_MC4500, CHAMBER_TYPE_ID_SC160LP, INLET_MANIFOLD_ID, OUTLET_MANIFOLD_ID } from "../types";
import { ManifoldDirection, ManifoldSpacer, ManifoldStructureType, ManifoldType } from "../manifolds";
import CurrentSelectedChamberSelectors from "../../reducers/current-selected-chamber-reducer";
import { ManifoldUtils } from "../manifolds/utils";
import ChamberService from "./chambers-service";
import { setInsertaTeeWarningType } from "../../actions/chamber-actions";

/**
 * Specific shared services for Manifolds
 * Functions should be kept clean and pure, avoiding side effects or data mutation
 * @type {{validateOutletOverIsolator: (function(*, *, *): boolean), testSelectedChambersBeforeRemoval: (function(*, *): boolean), validateInternalOutletOnPerimeterRow(*, *, *): boolean, validateIsolatorOverOutlet: (function(*, *, *): boolean), validateSelectedChamberIsActive: (function(*, *): boolean): (function(*, *=, *=, *=, *=, *, *): *), handleManifoldBuildRestrictions: (function(*=, *=): null), removeSelectedChambers: (function(*, *, *): boolean), getChamberCapAccessory: (function(*=, *=, *): ({id: null}|*)), validateInnerFreeSpace: (function(*, *, *, *=, *): boolean), ignoreAccessoryAttachment: (function(*=, *): boolean)}}
 */
const ManifoldService = {

  /**
   * Handles Manifold build rules.
   * @param id {string} - Type of manifold ID, i.e Inlet-Manifold | Outlet-Manifold
   * @param props - this.props from the component
   * @returns {null}
   */
  handleManifoldBuildRestrictions: (
    id = "",
    props
  ) => {

    if (id && id === "") return null;

    const currentChambersMetadata = CurrentSelectedChamberSelectors.getCurrentChambersInfo();
    const currentChamber = CurrentSelectedChamberSelectors.getChamberByPosition(props.row, props.col);
    const topChamber = CurrentSelectedChamberSelectors.getChamberByPosition(props.row, props.col - 1);
    const bottomChamber = CurrentSelectedChamberSelectors.getChamberByPosition(props.row, props.col + 1);
    const chamberTypeId = currentChambersMetadata["currentSelectedChamber"]["chamberType"]["id"];

    let isInner = currentChambersMetadata.currentSelectedChamber.isInnerChamber;

    // Fetch full construct for the manifold (includes sizing, position, flow rate, rules, etc.)
    const manifoldConstruct = ManifoldUtils.getManifoldConstruct(props.chamberType.id, id, props.totalStorageProvided, isInner);
    if (!manifoldConstruct || !manifoldConstruct.sizing) return null;
    if (ManifoldService.ignoreAccessoryAttachment(id, props)) return null;

    const chamberRow = props.chamberRows[props.row];
    const isFirstActive = currentChambersMetadata.currentSelectedChamber.isFirstActiveChamber;
    const isLastActive = currentChambersMetadata.currentSelectedChamber.isLastActiveChamber;

    // Should be upper or lower
    let isUpper = isFirstActive || props.upperCapActive;

    // if is lastActive should be placed down
    if (!isInner && isLastActive) isUpper = false;

    // if its inner and lowerCap active, then is lower
    if (isInner && props.lowerCapActive) isUpper = false;

    let accessory = isUpper
      ? { ...ManifoldService.getChamberCapAccessory(id, "upper", props), variant: manifoldConstruct.structureType }
      : { ...ManifoldService.getChamberCapAccessory(id, "lower", props), variant: manifoldConstruct.structureType };

    // Get the direction the assembly will build out
    let direction = ManifoldService.getDirection(props.row, props.chamberbed, false);

    if (isInner) { // It is a INTERNAL chamber, build an INTERNAL manifold
      // GENERAL VALIDATIONS
      // - Current chamber is active
      if (!ManifoldService.validateSelectedChamberIsActive(currentChamber, props)) return null;

      // - Upper/Lower chamber is removed prior adding a manifold, and has a cap
      if (!ManifoldService.validateInnerFreeSpace(currentChamber, topChamber, bottomChamber, isUpper, props)) return null;

      //Internal outlets can only be placed on the perimeter row of the bed
      if (!ManifoldService.validateInternalOutletOnPerimeterRow(chamberRow, accessory, props)) return null;

      // Do not add Outlet over isolator
      if (!ManifoldService.validateOutletOverIsolator(chamberRow, accessory, props)) return null;

      // Do not add Isolator over outlet
      if (!ManifoldService.validateIsolatorOverOutlet(chamberRow, accessory, props, isUpper)) return null;

      // Collect the rows that would be getting an assembly entry
      let currentRow = props.row;
      const numOfRowsInAssembly = manifoldConstruct.rules.placement.numberOfStartingStubs
        + ( manifoldConstruct.rules.assembly.skipFirstRow ? 1 : 0 );

      // Flip direction if second direct pipe found
      const rowIncrement = isUpper ? direction === 1 ? -1 : 1 : direction === 1 ? -1 : 1;
      let flipSide = false;

      let rowTestIncrement = 0;
      if (isUpper) {

        if (( direction === 2 )) {
          rowTestIncrement = 1;
        } else {
          rowTestIncrement = -1;
        }
      } else {
        if (( direction === 2 )) {
          rowTestIncrement = 1;
        } else {
          rowTestIncrement = -1;
        }
      }
      const rowIncremented = props.row + rowTestIncrement;

      if (CurrentSelectedChamberSelectors.isManifoldAttachedToChamber(rowIncremented, props.col))
        flipSide = true;

      if (CurrentSelectedChamberSelectors.isOppositeStubAttachedToChamber(rowIncremented, props.col))
        flipSide = true;

      // flip if nearby chamber is empty
      const moveRow = direction === 2 ? 1 : -1;
      if (!CurrentSelectedChamberSelectors.isTestedChamberActive(
        currentChambersMetadata.currentSelectedChamber.row + moveRow,
        currentChambersMetadata.currentSelectedChamber.chamberNumber
      ))
        flipSide = true;

      // Test if we can remove Chambers
      const config = ManifoldService.preparesConfigurationPayloadForVoid(
        isUpper,
        currentChambersMetadata.currentSelectedChamber.row,
        currentChambersMetadata.currentSelectedChamber.chamberNumber,
        props.chamberType.id,
        id,
        props.totalStorageProvided,
        ( !flipSide )
          ? direction
          : ManifoldService.getDirection(props.row, props.chamberbed, true, isUpper)
      );
      let voidArray = [];
      let mainRowArray = [];
      if (ManifoldService.testSelectedChambersBeforeRemoval(
        config,
        currentChambersMetadata.currentSelectedChamber.row,
        currentChambersMetadata.currentSelectedChamber.chamberNumber,
        props.chamberType.id,
        id,
        props.totalStorageProvided,
        props
      )) {

        voidArray = ManifoldService.buildManifoldVoidArray(config);
        mainRowArray = ManifoldService.getCoordinatesForManifoldActiveRowForMetadata(config);

        // Test for compliance on voidArray or mainRowArray, so it will not remove used chambers
        if (ManifoldService.testManifoldVoidArrayForConflicts(voidArray, "voidArray")) return null;

        if (ManifoldService.testRemovableChambersAndFlip(voidArray, props)) {
          // We need to regenerate and flip because of collision
          // ANd force FLIP
          flipSide = true;
          const config = ManifoldService.preparesConfigurationPayloadForVoid(
            isUpper,
            currentChambersMetadata.currentSelectedChamber.row,
            currentChambersMetadata.currentSelectedChamber.chamberNumber,
            props.chamberType.id,
            id,
            props.totalStorageProvided,
            ( !flipSide )
              ? direction
              : ManifoldService.getDirection(props.row, props.chamberbed, true, isUpper)
          );
          voidArray = ManifoldService.buildManifoldVoidArray(config);
          mainRowArray = ManifoldService.getCoordinatesForManifoldActiveRowForMetadata(config);

          if (ManifoldService.testManifoldVoidArrayForConflicts(voidArray, "voidArray")) return null;
        }

        // Generate void
        if (!ManifoldService.generateChamberVoid(voidArray, config.remove, props)) return null;

      } else {
        console.info("Chambers should not be removed");
        props.modalAlertActions({
          toggle: true,
          text: props.content[props.lang].showManifoldRequiredSpaceAlert,
          addClassName: "modal-alert"
        });
        return null;
      }

      isUpper
        ? props.setChamberUpperCapAccesory(props.row, props.col, accessory, true)
        : props.setChamberLowerCapAccesory(props.row, props.col, accessory, true);

      //props.setDirtyStateForMultiBeds(true);
      props.createMainManifold(props.row, props.col, null, flipSide);

      ChamberService.setChamberManifoldIdMeta(
        "isVoidArray", voidArray, props.row, props.col, props
      );

      ChamberService.setChamberManifoldIdMeta(
        "isManifoldConnectedArray", mainRowArray, props.row, props.col, props
      );

      if (chamberTypeId === CHAMBER_TYPE_ID_SC160LP && id === INLET_MANIFOLD_ID)
        props.modalAlertActions({
          toggle: true,
          text: props.content[props.lang].showInternalManifoldLowPositionAlertCollision,
          addClassName: "mid-height-alert",
          showAsHtml: true
        });

    } else {
      // ****** EXTERNAL manifolds !!! ******
      // Do not add Outlet over isolator
      if (!ManifoldService.validateOutletOverIsolator(chamberRow, accessory, props)) return null;

      // Do not add Isolator over outlet
      if (!ManifoldService.validateIsolatorOverOutlet(chamberRow, accessory, props, isUpper)) return null;


      if (!ManifoldService.validateTeeOverIsolator(chamberRow, accessory, props, isUpper)) {
        console.log("%c WE HAVE TEEEEE ", "color:red; background-color:yellow;");
      }

      let flipSide = false;
      // flip if nearby chamber is empty
      const moveRow = direction === 2 ? 1 : -1;

      if (!CurrentSelectedChamberSelectors.isTestedChamberActive(
        currentChambersMetadata.currentSelectedChamber.row + moveRow,
        currentChambersMetadata.currentSelectedChamber.chamberNumber
      )) {
        flipSide = true;
      }

      if (ManifoldService.hasOppositeChamberOnBuildDirection(props.row, props.col, direction, isUpper, props)) {
        // lets build void
        const config = ManifoldService.preparesConfigurationPayloadForVoid(
          isUpper,
          currentChambersMetadata.currentSelectedChamber.row,
          currentChambersMetadata.currentSelectedChamber.chamberNumber,
          props.chamberType.id,
          id,
          props.totalStorageProvided,
          ( !flipSide )
            ? direction
            : ManifoldService.getDirection(props.row, props.chamberbed, true, isUpper)
        );
        let voidArray = [];
        let mainRowArray = [];

        voidArray = ManifoldService.buildManifoldVoidArray(config);
        mainRowArray = ManifoldService.getCoordinatesForManifoldActiveRowForMetadata(config);

        // Generate void
        if (!ManifoldService.generateChamberVoid(voidArray, config.remove, props)) return null;
        setTimeout(() => {
          ChamberService.setChamberManifoldIdMeta(
            "isVoidArray", voidArray, props.row, props.col, props
          );
          ChamberService.setChamberManifoldIdMeta(
            "isManifoldConnectedArray", mainRowArray, props.row, props.col, props
          );
        }, 0);
      }

      isUpper
        ? props.setChamberUpperCapAccesory(props.row, props.col, accessory)
        : props.setChamberLowerCapAccesory(props.row, props.col, accessory);

      const config = ManifoldService.preparesConfigurationPayloadForVoid(
        isUpper,
        currentChambersMetadata.currentSelectedChamber.row,
        currentChambersMetadata.currentSelectedChamber.chamberNumber,
        props.chamberType.id,
        id,
        props.totalStorageProvided,
        ( !flipSide )
          ? direction
          : ManifoldService.getDirection(props.row, props.chamberbed, true, isUpper),
        isInner
      );

      let mainRowArray = [];

      mainRowArray = ManifoldService.getCoordinatesForManifoldActiveRowForMetadata(config);
      ChamberService.setChamberManifoldIdMeta(
        "isManifoldConnectedArray", mainRowArray, props.row, props.col, props
      );
      let voidArray = [];

      voidArray = ManifoldService.buildManifoldVoidArray(config);
      // ManifoldService.generateChamberVoid(voidArray, config.remove, props)
      /*ChamberService.setChamberManifoldIdMeta(
          "isVoidArray", voidArray, props.row, props.col, props,
      ); // NOTE: removed on a merge from manifolds to multibed, it might introduce a regression */
      //props.setDirtyStateForMultiBeds(true);
      props.createMainManifold(props.row, props.col, null, flipSide);
    }
    return null;
  },

  hasOppositeChamberOnBuildDirection: ( row, col, direction, isUpper, props ) => {
    try {
      const verticalIncrement = isUpper ? -1 : 1, testedColumn = col + verticalIncrement,
        horizontalIncrement = direction === 2 ? 1 : -1, testedRow = row + horizontalIncrement;
      return CurrentSelectedChamberSelectors.isTestedChamberActive(testedRow, testedColumn);
    } catch (error) {
      console.error(`ManifoldService.stubWouldHitSecondPipe() :: ${error.message}`);
      return false;
    }


  },

  /**
   *
   * @param isUpper {boolean} up or down position
   * @param row {number} - chamber coordinate
   * @param col {number}  - chamber coordinate
   * @param chamberTypeId {string} - Chamber Type Id i.e MC-4500
   * @param id {string} - Type of manifold ID, i.e Inlet-Manifold | Outlet-Manifold
   * @param totalStorageProvided {number}
   * @param direction {number}
   * @param isInner {boolean}
   * @returns {{numberOfChambers: number, removeRightToLeft, numberOfRows: number, removeTop, remove: boolean}}
   */
  preparesConfigurationPayloadForVoid: ( isUpper, row, col, chamberTypeId, id, totalStorageProvided, direction = 1, isInner = true ) => {
    try {
      const manifoldConstruct = ManifoldUtils.getManifoldConstruct(chamberTypeId, id, totalStorageProvided);
      if (!manifoldConstruct || !manifoldConstruct.sizing) return null;
      let { chambersPerRow, numberOfRows } = manifoldConstruct.rules.placement;
      const oppositeSideChambers = manifoldConstruct.rules.placement.oppositeSideChambers;

      //NOTE: Not sure why this was here, but removing it fixes the issue of outlets getting placed with zero stubs
      if (!isInner && id === "Outlet-Manifold" && manifoldConstruct.structureType === "nyloplast") {
        numberOfRows = 1;
      }

      return {
        removeTop: isUpper,
        removeRightToLeft: ( direction === 1 ),
        numberOfRows: numberOfRows,
        numberOfChambers: chambersPerRow,
        oppositeSideChambers: oppositeSideChambers,
        remove: true,
        chamberPosition: {
          row: row,
          col: col
        }
      };
    } catch (exception) {
      console.error(`ManifoldService.preparesConfigurationPayloadForVoid() :: ${exception.message}`);
      return undefined;
    }
  },

  /**
   * Returns the manifoldConstruct
   * @param chamberTypeId
   * @param id
   * @param totalStorageProvided
   * @returns {{sizing: {stubSizeNominal: number, stubSize: *, isolatorStubSize: *, trunkSize: *, trunkSizeNominal: number, manifoldSizeName: *, potentialSizes: *, structureSize: *, defaultManifoldSize: {}}, chamberTypeId: *, structureType: *, manifoldPosition: *, rules: {assembly: T, placement}, flowRates: {volumeFlowRateMinumum: *, trunkMaxFlowRate: *, volumeFlowRateMaximum: *, stubFlowRate: *}}}
   */
  getManifoldConstructPlacement: ( chamberTypeId, id, totalStorageProvided ) => {
    return ManifoldUtils.getManifoldConstruct(chamberTypeId, id, totalStorageProvided).rules.placement;
  },

  testManifoldVoidArrayForConflicts: ( chambersToRemoveArray, type = "voidArray" ) => {
    // Test if the chamber to be removed is used by a manifold
    for (let i = 0; i < chambersToRemoveArray.length; i++) {
      const chm = chambersToRemoveArray[i];
      if (type === "voidArray") { // test only the voidArray
        if (CurrentSelectedChamberSelectors.isManifoldAttachedToChamber(chm.row, chm.col)) {
          console.warn("Cannot remove chambers used by a manifold");
          return true;
        }
      } else {
        // Run tests for mainRowArray
      }
      return false;
    }
  },

  /**
   * Test and validates if the chambers should be removed
   * @example
   *     const config = {
   *          removeTop: true,
   *          removeRightToLeft: false,
   *          numberOfRows: 3,
   *          numberOfChambers: 3,
   *          remove: true
   *      }
   *
   * @param config
   * @param row
   * @param col
   * @returns {boolean}
   */
  testSelectedChambersBeforeRemoval: ( config, row, col ) => {
    try {
      const { removeTop, numberOfRows, numberOfChambers, removeRightToLeft } = config;
      const increment = removeTop ? -1 : 1;
      let selectedRow = row;
      let selectedCol = col + increment;
      let counterForColumns = 1;
      let counterForRows = 1;
      while (counterForRows < numberOfRows + 1) {
        while (counterForColumns < numberOfChambers + 1) {
          const testedChamber = CurrentSelectedChamberSelectors.getChamberByPosition(selectedRow, selectedCol);
          if (( testedChamber && testedChamber._upperCapAccessory ) || ( testedChamber && testedChamber._lowerCapAccessory )) {
            console.warn(
              `Accessory found, on row: ${testedChamber.row}  col: ${testedChamber.col}, manifold will not be added`
            );
            return true; // should be true, because it causes conflict on flipped manifolds. If something else is having a
            // conflict with this, this sould be discussed
          }
          selectedCol += increment;
          counterForColumns++;
        }
        selectedCol = col + increment; //  reset
        if (removeRightToLeft) {
          selectedRow += increment;
        } else {
          selectedRow -= increment;
        }

        counterForColumns = 1; //Reset and start again
        counterForRows++;
      }

    } catch (exception) {
      console.error(`ManifoldService.testSelectedChambersBeforeRemoval() :: ${exception.message}`);
      return false;
    }
    return true;
  },

  testRemovableChambersAndFlip( chambersArrayToTest ) {
    for (let i = 0; i < chambersArrayToTest.length; i++) {
      const chm = chambersArrayToTest[i];
      if (CurrentSelectedChamberSelectors.isManifoldAttachedToChamber(chm.row, chm.col)) {
        return true;
      }
    }
    return false;
  },

  /**
   * Sets empty space required by the manifold based on a grid
   * @example
   *     const config = {
   *          removeTop: true,
   *          removeRightToLeft: false,
   *          numberOfRows: 3,
   *          numberOfChambers: 3,
   *          remove: true,
   *          chamberPosition: {
   *            row: 0,
   *            col: 5
   *          }
   *      }
   *
   * @param config
   * @returns {[]}
   */
  buildManifoldVoidArray: ( config ) => {
    try {
      let {
        numberOfRows,
        numberOfChambers,
        removeTop,
        chamberPosition,
        removeRightToLeft,
        oppositeSideChambers
      } = config;
      const increment = removeTop ? -1 : 1;
      let selectedCol = ( oppositeSideChambers )
        //Adds the No of opposite chambers, taking into account the side of the build
        ? ( removeRightToLeft ) ? chamberPosition.row + oppositeSideChambers : chamberPosition.row - oppositeSideChambers
        : chamberPosition.row;
      let selectedRow = chamberPosition.col + increment;
      let runColsCounter = 1;
      let runRowsCounter = 1;
      let chamberCoordinatesArray = [];

      while (runRowsCounter < numberOfRows + 1 + oppositeSideChambers) {
        // creates space on Y
        while (runColsCounter < numberOfChambers + 1) {
          chamberCoordinatesArray.push({ col: selectedRow, row: selectedCol });
          selectedRow = ( removeTop ) ? selectedRow - 1 : selectedRow + 1;
          runColsCounter++;
        }
        // creates space on X
        selectedRow = ( removeTop ) ? chamberPosition.col - 1 : chamberPosition.col + 1;
        selectedCol = ( removeRightToLeft ) ? selectedCol - 1 : selectedCol + 1;
        runColsCounter = 1;
        runRowsCounter++;
      }
      return chamberCoordinatesArray;
    } catch (exception) {
      console.error(`ManifoldService.setManifoldVoidSize() :: ${exception.message}`);
      return [];
    }
  },


  /**
   * Counts the number of chambers over the row in order to help on void array build, when collision where placed
   * @param manifold
   * @param props
   * @returns {*[]|number}
   */
  countNumberOfChambersOverManifold: ( manifold, props ) => {
    try {
      const {
        _id, _isUpper, _letRow, _letColumn
      } = manifold, increment = _isUpper ? -1 : 1;
      let counter = 0, run = true, col = _letColumn + increment, row = _letRow,
        currentChamber = props.chamberbed.chamberRows[row]._chambers[col];
      while (run) {
        col += increment;
        currentChamber = props.chamberbed.chamberRows[row]._chambers[col];
        counter++;
        let voidArray = currentChamber._isVoidArray;
        run = voidArray.some(id => id === _id);
      }
      return counter;
    } catch (exception) {
      if (manifold._isManifoldInner)
        console.error(`ManifoldService.setManifoldVoidSize() :: ${exception.message}`);
      return [];
    }
  },
  /**
   * Tests if opposite Side Chamber metadata exists
   * @param manifold
   * @param props
   * @returns {boolean|*[]}
   */
  oppositeSideChambersOnManifoldRequired: ( manifold, props ) => {
    try {
      const {
        _isUpper, _letRow, _letColumn
      } = manifold, increment = _isUpper ? -1 : 1;
      let col = _letColumn + increment,
        row = _letRow - increment;
      if (!props.chamberbed.chamberRows[row] || !props.chamberbed.chamberRows[row]._chambers || !props.chamberbed.chamberRows[row]._chambers[col])
        return false;
      const currentChamber = props.chamberbed.chamberRows[row]._chambers[col];
      return currentChamber._isVoidArray.length > 0;
    } catch (exception) {
      console.error(`ManifoldService.oppositeSideChambersOnManifoldRequired() :: ${exception.message}`);
      return [];
    }
  },

  /**
   * Build the array of coordinates to claim space for new stubs on inner manifolds.
   * @param manifold
   * @param prevNumberOfStubs
   * @param numberOfRows
   * @param props
   * @returns { {stubsArray:[], size:number} }
   */
  buildArrayForStubs: ( manifold, prevNumberOfStubs, numberOfRows, props ) => {
    let selectedChambersArray = [];

    const {
      _isUpper, _direction, _spacer, _letRow, _letColumn, _isManifoldInner, _stubsNumber,
      _manifoldSize: { forceManifoldSpacer }
    } = manifold;

    const { chamberRows } = props;
    let rowsTraversed = 0;

    if (prevNumberOfStubs <= _stubsNumber) {
      let isLefty = ( _direction === 1 );
      if (!_isUpper) isLefty = ( _direction === 2 );
      const increment = _isUpper ? -1 : 1;
      let incrementSides = isLefty ? -1 : 1;
      if (!_isUpper) incrementSides *= -1;

      // Step 2. Generates the array of chambers to remove
      const colsToTraverse = chamberRows[_letRow]
        .chambers
        .filter(chamber => {
          return chamber.isVoidArray.indexOf(manifold.id) > -1;
        })
        .map(chamberCols => {
          return chamberCols._col;
        });

      let colsTraversed = 0;
      let currentRow = _letRow;
      let quit = false;
      while (rowsTraversed < numberOfRows && !quit) {
        let col = _letColumn + increment;
        if (!chamberRows[currentRow] || !chamberRows[currentRow].chambers)
          break;
        let thisRowsChambers = chamberRows[currentRow].chambers;
        if (!thisRowsChambers[_letColumn] || !thisRowsChambers[_letColumn].active)
          break;
        colsTraversed = 0;
        while (colsTraversed < colsToTraverse.length) {
          colsTraversed++;
          let coordinatesObj = {
            col: col,
            row: currentRow
          };
          let nextRow = chamberRows[currentRow];
          if (!nextRow || !nextRow.chambers) break;
          let nextChamber = nextRow.chambers[col];
          if (!nextChamber) break;
          const hasOtherManifold = nextChamber._isManifoldConnectedArray.filter(mc => mc !== manifold.id).length;
          const hasOtherVoid = nextChamber._isVoidArray.filter(mc => mc !== manifold.id).length;
          if (hasOtherManifold || hasOtherVoid) {
            // if we're running into chambers being used by other manifolds, remove that row from the array
            // eslint-disable-next-line no-loop-func
            let newArr = selectedChambersArray.filter(sc => sc.row !== currentRow
              && ( ( incrementSides > 0 && sc.row < currentRow )
                || ( incrementSides < 0 && sc.row > currentRow ) ));
            const lastRowAdded = newArr[newArr.length - 1].row;
            const distinctRows = Array.from(new Set(newArr.map(na => na.row))).length;
            if (manifold._spacer === ManifoldSpacer.gap && distinctRows > 1) {
              if (manifold._manifoldSize.forceManifoldSpacer && ( distinctRows % 2 === 0 )) {
                newArr = newArr.filter(na => na.row !== lastRowAdded);
              } else if (distinctRows % 2 > 0) {
                newArr = newArr.filter(na => na.row !== lastRowAdded);
              }
            }
            selectedChambersArray = newArr;
            // stop checking rows
            quit = true;
            // skip the rest of the row
            break;
          }
          if (!nextChamber.active) {
            selectedChambersArray.push(coordinatesObj);
            col += increment;
          } else if (forceManifoldSpacer === false && _spacer === ManifoldSpacer.gap && numberOfRows > 2) {
            selectedChambersArray.push(coordinatesObj);
            const nextRow = coordinatesObj.row + incrementSides;
            selectedChambersArray.push({ ...coordinatesObj, row: nextRow });
            col += increment;
            if (colsTraversed === colsToTraverse.length) {
              rowsTraversed++;
              currentRow += incrementSides;
            }
          } else if (forceManifoldSpacer === true && _spacer === ManifoldSpacer.noGap && _letColumn + increment === 1) {
            selectedChambersArray.push(coordinatesObj);
            selectedChambersArray.push({ ...coordinatesObj, row: coordinatesObj.row + incrementSides });
            col += increment;
            if (colsTraversed === colsToTraverse.length) {
              rowsTraversed++;
              currentRow += incrementSides;
            }
          } else if (forceManifoldSpacer === true && _spacer === ManifoldSpacer.noGap && _letColumn + increment !== 1) {
            selectedChambersArray.push(coordinatesObj);
            col += increment;
          } else if (forceManifoldSpacer === true && _spacer === ManifoldSpacer.gap) {
            selectedChambersArray.push(coordinatesObj);
            selectedChambersArray.push({ ...coordinatesObj, row: coordinatesObj.row + incrementSides });
            col += increment;
            if (colsTraversed === colsToTraverse.length) {
              rowsTraversed++;
              currentRow += incrementSides;
            }
          } else {
            selectedChambersArray.push(coordinatesObj);
            col += increment;
          }
        }
        currentRow += incrementSides;
        rowsTraversed++;
      }
    }

    return { stubsArray: selectedChambersArray, size: rowsTraversed-- };
  },

  /**
   * Generates the Void for the Inner manifold
   * @param chamberCoordinatesArray {[]}
   * @param remove  {boolean}
   * @param props - requires access to deactivateByArray and activateByArray trow props
   * @returns {boolean}
   */
  generateChamberVoid: ( chamberCoordinatesArray, remove = true, props ) => {
    try {
      if (remove) {
        props.deactivateByArray(chamberCoordinatesArray);
        // props.modifyChambersByArrayIsolator(chamberCoordinatesArray, 0, 0)  <<< aqui agregar el void
      } else {
        props.activateByArray(chamberCoordinatesArray);
      }
      return true;
    } catch (exception) {
      console.error(`ManifoldService.generateChamberVoid() :: ${exception.message}`);
      return false;
    }
  },

  /**
   * Get coordinates for manifold active rows for metadata
   * @param config -
   * @returns {[{row:number, col: number}]}
   */
  getCoordinatesForManifoldActiveRowForMetadata: ( config ) => {
    try {
      let selectedCol = config.chamberPosition.row;
      let selectedRow = config.chamberPosition.col;
      let runRows = 1;
      let chamberCoordinatesArray = [];
      while (runRows < config.numberOfRows + 1) {
        chamberCoordinatesArray.push({ col: selectedRow, row: selectedCol });
        selectedCol = ( config.removeRightToLeft ) ? selectedCol - 1 : selectedCol + 1;
        runRows++;
      }
      return chamberCoordinatesArray;
    } catch (exception) {
      console.error(`ManifoldService.getCoordinatesForManifoldActiveRowForMetadata() :: ${exception.message}`);
      return [];
    }
  },

  /**
   * Set chambers manifold metadata
   * @param metadataConfig {{chamberArray: *, meta: {manifoldId: *, usedByManifold: boolean}}} - requires access to modifyChambersByArray() action
   * @param actualRow {number} - coordinates -letRow- of the primary chamber where the manifold resides
   * @param actualCol {number} - coordinates -letColumn- of the primary chamber where the manifold resides
   * @param props {object} - requires access to modifyChambersByArray throw props
   */
  setChambersManifoldMetadata: ( metadataConfig, actualRow, actualCol, props ) => {
    try {
      props.modifyChambersByArray(
        metadataConfig.chamberArray,
        actualRow,
        actualCol,
        { chamberMeta: metadataConfig.meta }
      );
    } catch (exception) {
      console.error(`ManifoldService.setChambersManifoldMetadata() :: ${exception.message}`);
    }
  },

  // EXTERNAL VALIDATIONS
  /**
   * Do not add Outlet over isolator
   * @param chamberRow
   * @param accessory
   * @param props
   * @returns {boolean}
   */
  validateOutletOverIsolator: ( chamberRow, accessory, props ) => {
    if (chamberRow.chambers[props.col].isIsolatorArray.length && accessory.id === "Outlet-Manifold") {
      props.modalAlertActions({
        toggle: true,
        text: props.content[props.lang]["notAddOutletOverIsolator"]
      });
      return false;
    }
    return true;
  },

  /**
   * Do not add Isolator over outlet
   * @param chamberRow
   * @param accessory
   * @param props
   */
  validateIsolatorOverOutlet: ( chamberRow, accessory, props, isUpper ) => {
    const increment = isUpper ? 1 : -1;
    let current = props.col;
    let prevent = false;
    let done = false;
    let secondPipeGap = false;

    while (!done && !prevent) {
      const chamber = chamberRow.chambers[current];
      if (chamber && chamber.active && secondPipeGap) {
        secondPipeGap = false;
      }
      if (!chamber || !chamber.active) {
        if (!secondPipeGap) {
          done = true;
        }
      } else if (chamber.upperCapAccessory !== null &&
        ( ( chamber.upperCapAccessory.id === "Outlet-Manifold" && accessory.id === "Inlet-Manifold" ) ||
          ( chamber.upperCapAccessory.id === "Inlet-Manifold" && accessory.id === "Outlet-Manifold" ) )) {
        prevent = true;
      } else if (chamber.lowerCapAccessory !== null &&
        ( ( chamber.lowerCapAccessory.id === "Outlet-Manifold" && accessory.id === "Inlet-Manifold" ) ||
          ( chamber.lowerCapAccessory.id === "Inlet-Manifold" && accessory.id === "Outlet-Manifold" ) )) {
        prevent = true;
      }

      if (chamber && chamber._isOppositeStubAttached) {
        done = false;
        secondPipeGap = true;
      }

      current += increment;
    }
    if (prevent) {
      props.modalAlertActions({
        toggle: true,
        text: props.content[props.lang]["notAddIsolatorOverOutlet"]
      });
      return false;
    }
    return true;
  },


  validateTeeOverIsolator: ( chamberRow, accessory, props, isUpper ) => {
    const increment = isUpper ? 1 : -1;
    let current = props.col;
    let prevent = false;
    let done = false;
    let isInlet = ( accessory && accessory.id === "Inlet-Manifold" );

    while (!done && !prevent) {
      const chamber = chamberRow.chambers[current];

      //Check if there is an Inserta Tee
      if (
        chamber
        && chamber.active
        && chamber._accessory
        && chamber._accessory.id
        && chamber._accessory.id === "Inserta-tee"
        && isInlet
      ) {
        done = true;
        prevent = true;
      }

      // Stop on chambers undefined

      if (chamber && !chamber.active) {
        done = true;
      }

      if (!chamber) {
        done = true;
      }


      current += increment;
    }
    if (prevent) {
      // props.modalAlertActions({
      //     toggle: true,
      //     text: this.props.content[this.props.lang].showInternalManifoldLowPositionAlertCollision,
      //     addClassName: "mid-height-alert",
      //     showAsHtml: true,
      //     btnText: this.props.content[this.props.lang].cancel,
      //     secondButton: false,
      // });
      props.setInsertaTeeWarningType("MANIFOLD_CREATE");
      props.showInsertaTeeWarning(true);
      return false;
    }
    return true;
  },


  // INTERNAL VALIDATIONS
  /**
   * Validates that the selected chamber is active
   * @param currentChamber
   * @param props - React props object
   * @returns {boolean}
   */
  validateSelectedChamberIsActive: ( currentChamber, props ) => {
    if (!currentChamber.active) {
      props.modalAlertActions({
        toggle: true,
        text: props.content[props.lang].internalManifoldsOnActiveChambesOnly
      });
      return false;
    }
    return true;
  },

  /**
   * Validates free space
   * 1. That a chamber is removed prior adding a manifold
   * 2. That the selected chamber, has a cap
   * @param currentChamber
   * @param topChamber
   * @param bottomChamber
   * @param isUpper
   * @param props
   * @returns {boolean}
   */
  validateInnerFreeSpace: ( currentChamber, topChamber, bottomChamber, isUpper, props ) => {
    if (( bottomChamber.active && topChamber.active )
      || ( isUpper && !currentChamber.upperCapActive )
      || ( !isUpper && !currentChamber.lowerCapActive )) {
      props.modalAlertActions({
        toggle: true,
        text: props.chamberType.id === CHAMBER_TYPE_ID_MC4500
          ? props.content[props.lang].removeChamberBeforeAddingManifoldMC4500
          : props.content[props.lang].removeChamberBeforeAddingManifold
      });
      return false;
    }
    return true;
  },

  /**
   * Internal outlets can only be placed on the perimeter row of the bed
   * @param chamberRow
   * @param accessory
   * @param props - React props object
   * @returns {boolean}
   */
  validateInternalOutletOnPerimeterRow( chamberRow, accessory, props ) {
    if (accessory.id === "Outlet-Manifold" &&
      chamberRow.row !== 0 &&
      chamberRow.row !== props.chamberRows.length - 1
    ) {
      props.modalAlertActions({
        toggle: true,
        text: props.content[props.lang].internalOutletsOnPerimeterOnly
      });
      return false;
    }
    return true;
  },

  // OTHERS
  /**
   * Tests if Accessory attachment should be ignored
   * @param id
   * @param props
   * @returns {boolean}
   */
  ignoreAccessoryAttachment: ( id, props ) => {
    try {
      const accessory = props.getChamberAccessoryMap().get(id);
      const exception = accessory.exceptions[props.chamberType.id];
      if (exception && !exception.attachable) return true;
    } catch (exception) {
      console.warn(`An issue occurred checking the accessory's exceptions :: ${exception.message}`);
    }
    return false;
  },

  /**
   * Retrieves Chamber Accessory data for upper or lower structures
   * @param id Type Id for the manifold
   * @param type 'upper' | 'lower'
   * @param props react props object
   * @returns {{id: null}|*}
   */
  getChamberCapAccessory: ( id, type = "upper", props ) => {
    if (!props[`${type}CapAccessory`] || props[`${type}CapAccessory`].id !== id) {
      return props.getChamberAccessoryMap().get(id);
    }
    return { id: null };
  },

  // MANIFOLD HELPER FUNCTIONS
  /**
   * Tells if the manifold is an Inner or otter manifold
   * @param coordinates
   * @param chamberRows
   * @returns {boolean}
   */
  isManifoldInnerOnChamberRow: ( coordinates, chamberRows ) => {
    const col = coordinates.col;
    const row = coordinates.row;
    const chambersByCol = chamberRows[row];
    const firstActiveChamber = chambersByCol.getFirstActiveChamber();
    const lastActiveChamber = chambersByCol.getLastActiveChamber();
    const isFirstActiveChamber = firstActiveChamber &&
      firstActiveChamber.col === col;
    const isLastActiveChamber = lastActiveChamber &&
      lastActiveChamber.col === col;
    return !isFirstActiveChamber &&
      !isLastActiveChamber;
  },

  /**
   * Used by the Manifold Class as a helper function to the actual assembly metadata
   * @param assemblyManifest
   * @returns {*}
   */
  getAssembly: ( assemblyManifest ) => {
    return assemblyManifest
      .map(( assemblyPart, i ) => {
        let manifoldType = null;
        if (i === 0 && i === assemblyManifest.length - 1) {
          manifoldType = ManifoldType.StraightVerticalPipe;
        } else if (i === 0) {
          manifoldType = ManifoldType.RightElbow;
        } else if (i === assemblyManifest.length - 1) {
          manifoldType = ManifoldType.LeftElbow;
        } else if (assemblyPart.enter) {
          manifoldType = ManifoldType.TeePipe;
        } else {
          manifoldType = ManifoldType.StraightHorizontalPipe;
        }
        return {
          rowIndex: assemblyPart.rowIndex,
          manifoldType: manifoldType,
          structureType: assemblyPart.structure
        };
      });
  },

  // BASIC HELPERS
  /**
   * returns the direction to build the void depending on the number of rows
   * @param rootRow
   * @param chamberbed
   * @param flip {boolean} - forces flips side
   * @returns {number}
   */
  getDirection: ( rootRow, chamberbed, flip = false, isUpper = true ) => {
    let { numOfRows } = chamberbed;
    const middleValue = numOfRows / 2;
    let direction = ManifoldDirection.Left;
    direction = middleValue <= rootRow
      ? ( !flip ) ? ManifoldDirection.Left : ManifoldDirection.Right
      : ( !flip ) ? ManifoldDirection.Right : ManifoldDirection.Left;
    return direction;
  },

  /**
   * Test if chamber is connected to second direct pipe
   * @param chamberRow
   * @param endCapChamber
   * @param isUpper
   * @param manifolds
   * @returns {boolean}
   */
  isChamberConnectedToSecondDirectPipe: ( chamberRow, endCapChamber, isUpper, manifolds ) => {
    const increment = isUpper ? -1 : 1;
    let current = endCapChamber.col + increment;
    if (!chamberRow.chambers[current] || chamberRow.chambers[current].active)
      return false;
    let done = false;
    while (!done) {
      const thisChamber = chamberRow.chambers[current];
      if (thisChamber && thisChamber.active) {
        const manifoldId = thisChamber[isUpper ? "lowerCapAccessory" : "upperCapAccessory"]
          ? thisChamber[isUpper ? "lowerCapAccessory" : "upperCapAccessory"].manifoldId
          : null;
        if (manifoldId) {
          const match = manifolds.find(m => m.id === manifoldId);
          if (match && match.hasSecondPipe && match.hasIsolator)
            return true;
        }
        done = true;
      } else if (!thisChamber) {
        done = true;
      }
      current += increment;
    }
    return false;
  },

  /**
   * Test if the opposite chamber contains a manifold
   * @param manifolds
   * @param chamberRow
   * @param letColumn
   * @param isUpper
   * @returns {boolean}
   */
  secondPipeWouldHitManifold: ( manifolds, chamberRow, letColumn, isUpper ) => {
    try {
      const increment = isUpper ? -1 : 1;
      let nextActiveChamber = chamberRow.chambers[letColumn + increment];
      let hits = false, done = false;
      while (!hits && !done && nextActiveChamber) {
        if (nextActiveChamber.active) {
          done = true;
          for (let manifoldIndex = 0; manifoldIndex < manifolds.length; manifoldIndex++) {
            let manifold = manifolds[manifoldIndex];
            for (let assemblyIndex = 0; assemblyIndex < manifolds[manifoldIndex]._assembly.length; assemblyIndex++) {
              let assembly = manifold._assembly[assemblyIndex];
              if (assembly.rowIndex === chamberRow.row
                && manifold._letColumn === nextActiveChamber._col) {
                hits = true;
              }
            }
          }
        }
        nextActiveChamber = chamberRow.chambers[nextActiveChamber._col + increment];
      }
      return hits;
    } catch (error) {
      console.error(`ManifoldService.secondPipeWouldHitManifold() :: ${error.message}`);
      return false;
    }
  },

  /**
   * Test if opposite chamber contains a direct pipe
   * @param manifolds
   * @param chamberRow
   * @param stubColumn
   * @param isUpper
   * @returns {boolean}
   */
  stubWouldHitSecondPipe: ( manifolds, chamberRow, stubColumn, isUpper ) => {
    try {
      const increment = isUpper ? -1 : 1;
      let hits = false, done = false;
      if (!chamberRow || !chamberRow.chambers)
        throw new Error(`The provided chamber row is undefined and does not exist in the chamber bed.`);
      let nextChamber = chamberRow.chambers[stubColumn + increment];
      while (!hits && !done && nextChamber) {
        if (nextChamber.active) {
          done = true;
          // eslint-disable-next-line no-loop-func
          if (manifolds.find(manifold => manifold._letRow === chamberRow.row
            && manifold._letColumn === nextChamber._col
            && manifold._hasSecondPipe)) {
            hits = true;
          }
        }
        nextChamber = chamberRow.chambers[nextChamber._col + increment];
      }
      return hits;
    } catch (error) {
      console.error(`ManifoldService.stubWouldHitSecondPipe() :: ${error.message}`);
      return false;
    }
  },

  /**
   * Generates the meta for the assembly on the update manifold process
   * @param manifold
   * @param state
   * @param payload
   * @returns {*[]|{assemblyManifest, assembly: *, newManifoldValues}}
   */
  getAssemblyMeta: ( manifold, state, payload ) => {
    try {
      let {
        chamberType,
        chamberRowSpacing,
        totalStorageProvided,
        chamberbed,
        electedManifoldMetadata
      } = payload;
      let manifoldOffset = electedManifoldMetadata.manifoldOffset;
      const thisManifoldsRow = chamberbed.chamberRows[electedManifoldMetadata.letRow];

      let structureType;
      thisManifoldsRow.chambers
        .forEach(chamber => {
          if (
            chamber &&
            chamber.upperCapActive &&
            chamber.upperCapAccessory
          ) {
            if (
              chamber.upperCapAccessory.id === INLET_MANIFOLD_ID ||
              chamber.upperCapAccessory.id === OUTLET_MANIFOLD_ID
            ) {
              structureType = chamber.upperCapAccessory.variant;
            }
          }
          if (
            chamber &&
            chamber.lowerCapActive &&
            chamber.lowerCapAccessory
          ) {
            if (
              chamber.lowerCapAccessory.id === INLET_MANIFOLD_ID ||
              chamber.lowerCapAccessory.id === OUTLET_MANIFOLD_ID
            ) {
              structureType = chamber.lowerCapAccessory.variant;
            }
          }
        });

      let structureSize = ( electedManifoldMetadata.manifoldSize )[structureType === "nyloplast"
        ? "nyloplastStructureSize"
        : "concreteStructureSize"];

      if (!structureSize) {
        const manifoldDataset = ManifoldUtils.getManifoldDatasetByChamberId(
          chamberType.id,
          electedManifoldMetadata.manifoldSize.type,
          structureType === "nyloplast"
        );

        const matchingSize = manifoldDataset
          .find(manifoldDs => manifoldDs.size === electedManifoldMetadata.manifoldSize.size);

        if (matchingSize)
          structureSize = matchingSize[structureType === "nyloplast"
            ? "nyloplastStructureSize"
            : "concreteStructureSize"];
      }

      const manifoldMetadata = ManifoldUtils.getManifoldMetadataByTypeAndSize(structureType, structureSize);

      manifoldOffset = manifoldMetadata &&
        ( manifoldMetadata.outerDiameter / 12 ) / 2 > chamberType.width / 2 + chamberRowSpacing;

      const { forceManifoldSpacer } = manifold._manifoldSize;

      const { actualRows: assemblyManifest, newManifoldValues } = ManifoldService
        .findPotentialRowsForAssemblyManifest(
          manifold,
          chamberbed.chamberRows,
          manifoldOffset,
          state.extraInletStubs,
          state.extraOutletStubs,
          forceManifoldSpacer,
          manifold.isUpper
        );


      let manifoldTypeId = ( manifold.isInlet )
        ? "INLET_MANIFOLD_ID"
        : "OUTLET_MANIFOLD_ID";

      const constructPlacement = ManifoldService.getManifoldConstructPlacement(
        chamberType.id,
        manifoldTypeId,
        totalStorageProvided);


      let firstTimeManifold = false;
      if (manifold._isManifoldInner && !manifold._userModified) firstTimeManifold = true;

      let assembly = ManifoldService.getAssembly(assemblyManifest);

      const stubAssemblies = assembly
        .filter(manifestPotentialRow => {
          return manifestPotentialRow.structureType === null &&
            manifestPotentialRow.manifoldType !== ManifoldType.StraightHorizontalPipe;
        });

      newManifoldValues["_stubsNumber"] = ( stubAssemblies || [] ).length;

      if (!newManifoldValues["_direction"]) {
        newManifoldValues["_direction"] = !manifold._isUpper
          ? assembly[0].manifoldType === ManifoldType.StraightVerticalPipe ||
          assembly[0].manifoldType === ManifoldType.RightElbow
            ? ManifoldDirection.Right
            : ManifoldDirection.Left
          : assembly[0].manifoldType === ManifoldType.StraightVerticalPipe ||
          assembly[0].manifoldType === ManifoldType.LeftElbow
            ? ManifoldDirection.Left
            : ManifoldDirection.Right;
      }

      if (firstTimeManifold && ( stubAssemblies || [] ).length > 0) this._stubsNumber = constructPlacement["numberOfStartingStubs"];

      newManifoldValues["_assembly"] = assembly;
      return { assembly, assemblyManifest, newManifoldValues };
    } catch (exception) {
      console.error(`ManifoldService.getAssemblyMeta() :: ${exception.message}`);
      return [];
    }
  },

  /**
   * Finds potential rows for the new manifolds when updating
   * @param manifold
   * @param chamberRows
   * @param isRowOffset
   * @param extraInletStubs
   * @param extraOutletStubs
   * @param forceManifoldSpacerNotInUse
   * @param isUpper
   * @returns {{actualRows: (*), newManifoldValues: {_direction}}|*[]}
   */
  findPotentialRowsForAssemblyManifest: (
    manifold,
    chamberRows,
    isRowOffset,
    extraInletStubs,
    extraOutletStubs,
    forceManifoldSpacerNotInUse,
    isUpper = true
  ) => {
    try {

      const { forceManifoldSpacer } = manifold._manifoldSize;
      isRowOffset = forceManifoldSpacer;

      let newManifoldValues = {
        _direction: manifold["_direction"]
      };

      let extraStubs = manifold.isInlet ? extraInletStubs : extraOutletStubs;

      let potentialRows = ManifoldService.findPotentialLimitedRows(chamberRows, isUpper, false, manifold);

      let potentialRowAverageRow = potentialRows.reduce(( last, row ) => last + row.row, 0) / potentialRows.length;

      if (manifold._userModified) {
        extraStubs = manifold._stubsNumber;
      }

      let isForwardDirection = ManifoldService.isForwardDirectionValidate(manifold, potentialRowAverageRow);

      potentialRows = isForwardDirection ?
        potentialRows :
        potentialRows.reverse();

      // Calculates if its the stubs will be placed on continuous chamber rows.
      // 1 = no spacer, 2 = spaced out.
      let spacer;
      if (manifold._userModified) {
        spacer = manifold._spacer === ManifoldSpacer.gap ? ManifoldSpacer.gap : ManifoldSpacer.noGap;
      } else {
        spacer = ( extraStubs !== 1 && extraStubs <= Math.ceil(( potentialRows.length - ( isRowOffset ? 2 : 1 ) ) / 2) )
          ? ManifoldSpacer.gap
          : ManifoldSpacer.noGap;
      }
      manifold._spacer = spacer;

      let letStubs = 1;

      let { actualRows, thatManifold } = ManifoldService
        .getActualRows(
          potentialRows,
          extraStubs,
          isUpper,
          letStubs,
          isRowOffset,
          spacer,
          manifold
        );

      // Special case where we force the manifold spacer, but can't put in another row
      // if (forceManifoldSpacer && actualRows.length > 1 && (actualRows.length % spacer === 0) && !manifold._isManifoldInner) {
      //     console.log("Scenario A Row Popped!");
      //     actualRows.pop();
      // }
      // // If you have a row offset (skip) and the actualRows.length === 2, remove the last of its assembly
      // if (isRowOffset && actualRows.length === 2 && !manifold._isManifoldInner) {
      //     console.log("Scenario B Row Popped!");
      //     actualRows.pop();
      // }
      return { actualRows: isForwardDirection ? actualRows : actualRows.reverse(), newManifoldValues };
    } catch (exception) {
      console.error(`ManifoldService.findPotentialRowsForAssemblyArray() :: ${exception.message}`);
      return [];
    }
  },

  /**
   * Returns true or false to forward direction, and
   * is used on findPotentialRowsForAssemblyManifest() and findPotentialRows()
   *
   * @param manifold
   * @param potentialRowAverageRow
   * @returns {boolean|number}
   */
  isForwardDirectionValidate: ( manifold, potentialRowAverageRow ) => {
    try {
      if (manifold._direction === ManifoldDirection.None) {
        // Change mde to manifolds should render this piece of code unusable,
        // we are deciding the direction based on teh side of the bed the manifold is created
        /**
         * testing its use, if debugger is hit, pleased remove this and add a jsdoc.
         * If not remove after Sep 2021
         */
        console.log("%c <<< manifold._direction === ManifoldDirection.None >>> ", "background: yellow; color: red");
        debugger;
        const isForwardDirection = manifold._letRow <= potentialRowAverageRow;

        // This returns direction not isForward direction, but this piece of cod should not be used any more
        // manifold._direction = ...
        return !manifold._isUpper
          ? isForwardDirection ? ManifoldDirection.Left : ManifoldDirection.Right
          : isForwardDirection ? ManifoldDirection.Right : ManifoldDirection.Left;
      } else {
        if (!manifold._isUpper) {
          if (manifold._direction === 1) {
            if (manifold._isInlet) {
              if (manifold._isManifoldInner) {
                return manifold._direction === ManifoldDirection.Right;
              } else {
                return manifold._direction === ManifoldDirection.Right;
              }
            } else {
              return manifold._direction === ManifoldDirection.Right;
            }
          }
          if (manifold._direction === 2) {
            if (manifold._isInlet) {
              if (manifold._isManifoldInner) {
                return manifold._direction === ManifoldDirection.Right;
              } else {
                return manifold._direction === ManifoldDirection.Right;
              }
            } else {
              if (manifold._isManifoldInner) {
                return manifold._direction === ManifoldDirection.Right;
              } else {
                return manifold._direction === ManifoldDirection.Right;
              }
            }
          }
        } else {
          if (manifold._direction === 1) {
            return manifold._direction === ManifoldDirection.Right;
          }
          if (manifold._direction === 2) {
            return manifold._direction === ManifoldDirection.Right;
          }
        }
      }
    } catch (error) {
      console.error(`ManifoldService.isForwardDirectionValidate() :: ${error.message}`);
    }
  },

  /**
   * Used by the Manifold Class as a helper function for upper manifolds
   * @param chamberRows
   * @param isUpper
   * @param flip
   * @param that
   * @returns {*}
   */
  findPotentialLimitedRows: (
    chamberRows,
    isUpper,
    flip = false,
    that
  ) => {
    let reduceFunction = isUpper
      ? that.simpleFilterReductionUpper.bind(that)
      : that.simpleFilterReductionLower.bind(that);

    let start = 0, end = 0;
    try {
      start = chamberRows.slice(0, that.letRow + 1).reverse().reduce(reduceFunction, that.letRow);
      end = chamberRows.slice(that.letRow).reduce(reduceFunction, that.letRow);
    } catch (exception) {
      console.warn("Couldn't derive a start or end for manifold");

      start = that.letRow;
      end = that.letRow;
    }

    let potentialRowAverageRow = ( start + end ) / 2;  // Build an average

    let canRunLeft = true, canRunRight = true;
    if (chamberRows[that._letRow + 1] && chamberRows[that._letRow + 1]._chambers && chamberRows[that._letRow + 1]._chambers[that._letColumn])
      canRunLeft = chamberRows[that._letRow + 1]._chambers[that._letColumn]._active;

    if (chamberRows[that._letRow - 1] && chamberRows[that._letRow - 1]._chambers && chamberRows[that._letRow - 1]._chambers[that._letColumn])
      canRunRight = chamberRows[that._letRow - 1]._chambers[that._letColumn]._active;

    if (
      ( that._direction === ManifoldDirection.Left && !that._isUpper ) ||
      ( that._direction === ManifoldDirection.Right && that._isUpper )) {
      if (!flip) {
        if (that._direction === 1) {
          // Lower, Left(1), Not Flipped - OK
          return chamberRows.slice(start, that.letRow + 1);
        }
        if (that._direction === 2) {
          // Upper, Right(1), Not Flipped - OK
          return chamberRows.slice(that.letRow, end + 1);
        }
      } else {
        // FLIPPED
        if (that._direction === 1) {
          // Upper, Left(1), Flipped  - OK
          return chamberRows.slice(start, that.letRow + 1);
        }
        if (that._direction === 2) {
          // Upper, Right(2), Flipped  - OK
          return chamberRows.slice(that.letRow, end + 1);
        }
      }
    } else if (
      ( that._direction === ManifoldDirection.Right && !that._isUpper ) ||
      ( that._direction === ManifoldDirection.Left && that._isUpper )
    ) {
      if (!flip) {
        if (that._direction === 1) {
          // Upper, Left(1), Not Flipped  - OK
          return chamberRows.slice(start, that.letRow + 1);
        }
        if (that._direction === 2) {
          // Lower, Right(2), Not Flipped
          // console.log('Lower, Right(2), Not Flipped')
          return chamberRows.slice(that.letRow, end + 1);
        }
      } else {
        // FLIPPED
        if (that._direction === 1) {
          // Upper, Left(1), Flipped  - OK
          return chamberRows.slice(start, that.letRow + 1);
        }
        if (that._direction === 2) {
          // Lower, Right(2), Flipped  - OK
          return chamberRows.slice(that.letRow, end + 1);
        }
      }
    } else {
      // Direction on 0 = NONE

      // Change mde to manifolds should render this piece of code unusable,
      // we are deciding the direction based on teh side of the bed the manifold is created
      /**
       * testing its use, if debugger is hit, pleased remove this and add a jsdoc.
       * If not remove after Sep 2021
       */
      console.log("%c <<< manifold._direction === ManifoldDirection.None >>> ", "background: yellow; color: red");
      debugger;

      if (!flip) {
        return potentialRowAverageRow > that.letRow
          ? chamberRows.slice(that.letRow, end + 1)
          : chamberRows.slice(start, that.letRow + 1);
      } else {
        return potentialRowAverageRow >= that.letRow
          ? canRunRight
            ? chamberRows.slice(start, that.letRow + 1)
            : chamberRows.slice(that.letRow, end + 1)
          : canRunLeft
            ? chamberRows.slice(that.letRow, end + 1)
            : chamberRows.slice(start, that.letRow + 1);
      }
    }
  },

  /**
   * Used by the Manifold Class as a helper function to returning Chamber bed rows
   * @param potentialRows
   * @param extraStubs {number} - The number of stubs we need to place
   * @param isUpper
   * @param letStubs
   * @param isRowOffset
   * @param spacer
   * @param that
   * @returns {*}
   */
  getActualRows: (
    potentialRows,
    extraStubs,
    isUpper,
    letStubs,
    isRowOffset,
    spacer,
    that
  ) => {
    // read and force forceManifoldSpacer and isRowOffset
    const { forceManifoldSpacer } = that._manifoldSize;
    isRowOffset = forceManifoldSpacer;

    try {
      let stubsNumberToAdd = Number(that._stubsNumber || extraStubs), rulesSet = false;

      if (forceManifoldSpacer === true && that._spacer === 1) { // TESTED
        stubsNumberToAdd += 2;
        // if (!that.isManifoldInner) // For some reason this is needed because the behavior changes between inner and external manifolds
        //     stubsNumberToAdd += 1; // fix for case on #1370674062 - Manifold stub count not correct for 310 system
        rulesSet = true;
      }

      if (forceManifoldSpacer === true && that._spacer === 2) { // TESTED
        stubsNumberToAdd *= 2;
        ++stubsNumberToAdd;
        rulesSet = true;
      }

      // forceManifoldSpacer === false cases
      if (forceManifoldSpacer === false && that._spacer === 1) { // TESTED
        stubsNumberToAdd += 1;
        rulesSet = true;
      }

      if (forceManifoldSpacer === false && that._spacer === 2) { // TESTED
        stubsNumberToAdd *= 2;
        rulesSet = true;
      }

      if (that._userModified) {
        extraStubs = stubsNumberToAdd;
        if (forceManifoldSpacer === true && that._spacer === 1 && that._stubsNumber === 0) {
          extraStubs -= 2;
        }
        if (forceManifoldSpacer === true && that._spacer === 2 && that._stubsNumber === 1) {
          extraStubs += 1;
        }
        console.warn("ExtraStubs changed to:", extraStubs);
      }
      let actualRowsArray = { actualRows: [], thatManifold: that };

      if (rulesSet) {
        actualRowsArray = {
          actualRows: potentialRows
            .map(( potentialRow, idx ) => {
              let structure = null;

              // STEP 1. Attach structure to the first or last stub
              // upperManifold
              if (isUpper && potentialRow.row === that.letRow) {
                let firstChamber = potentialRow.getActiveChamberByCoordinates(that.coordinates);
                if (firstChamber && firstChamber.upperCapAccessory !== null) {
                  let id = firstChamber.upperCapAccessory.id;
                  let variant = firstChamber.upperCapAccessory.variant;

                  if (id === INLET_MANIFOLD_ID || id === OUTLET_MANIFOLD_ID) {
                    switch (variant) {
                      case "round":
                        structure = ManifoldStructureType.Round;
                        break;
                      case "square":
                        structure = ManifoldStructureType.Square;
                        break;
                      case "nyloplast":
                        structure = ManifoldStructureType.Nyloplast;
                        break;
                      default:
                        break;
                    }
                  }
                }
              }

              // lowerManifold
              else if (!isUpper && potentialRow.row === that.letRow) {
                let lastChamber = potentialRow.getActiveChamberByCoordinates(that.coordinates);

                if (lastChamber && lastChamber.lowerCapAccessory !== null) {
                  let id = lastChamber.lowerCapAccessory.id;
                  let variant = lastChamber.lowerCapAccessory.variant;

                  if (id === INLET_MANIFOLD_ID || id === OUTLET_MANIFOLD_ID) {
                    switch (variant) {
                      case "round":
                        structure = ManifoldStructureType.Round;
                        break;
                      case "square":
                        structure = ManifoldStructureType.Square;
                        break;
                      case "nyloplast":
                        structure = ManifoldStructureType.Nyloplast;
                        break;
                      default:
                        break;
                    }
                  }
                }
              }

              // STEP 2. Check rules to Force Manifold Spacer
              if (idx >= stubsNumberToAdd && stubsNumberToAdd !== 0) return null;

              // STEP 3. Add stubs that comply with the rules
              if (that._letRow === potentialRow.row) {
                // It's the direct connection to the basin
                --letStubs;
                return {
                  rowIndex: potentialRow.row,
                  structure: structure,
                  isLockedStub: true,
                  enter: true
                };
              } else if (extraStubs && isRowOffset && idx === 1) {
                // First row after the basin that's being skipped
                return {
                  rowIndex: potentialRow.row,
                  structure: structure,
                  isLockedStub: true,
                  enter: false
                };
              } else if (extraStubs) {
                if (( idx - 1 + ( isRowOffset ? 1 : 0 ) ) % spacer === 0) {
                  //should add a new stub
                  const ret = {
                    rowIndex: potentialRow.row,
                    structure: structure,
                    isLockedStub: false,
                    enter: true
                  };
                  // --extraStubs;
                  //++that._stubsNumber;
                  return {
                    rowIndex: potentialRow.row,
                    structure: structure,
                    isLockedStub: false,
                    enter: true
                  };
                } else {
                  //should omit a new stub and jump the row
                  return {
                    rowIndex: potentialRow.row,
                    structure: structure,
                    isLockedStub: false,
                    enter: false
                  };
                }
              } else if (letStubs) {
                //should omit a new stub and jump the row
                return {
                  rowIndex: potentialRow.row,
                  structure: structure,
                  isLockedStub: false,
                  enter: false
                };
              }

              // its not a stub so its omitted
              return null;
            }).filter(Boolean), thatManifold: that
        };
      } else {
        console.info("+++++++++ RULES NOT SET");
      }
      return actualRowsArray;
    } catch (error) {
      console.error(`ManifoldService.getActualRows() :: ${error.message}`);
      return { actualRows: [], thatManifold: that };
    }
  }
};

export default ManifoldService;



// WEBPACK FOOTER //
// ./src/lib/services/manifold-service.js